"use client";

/**
 * Auth Context
 * @apal/shared-auth
 *
 * Provides authentication state and actions to the component tree.
 * This is a generic auth context - specific implementations (API calls)
 * are injected via the AuthProvider props.
 */

import * as React from "react";
import type {
  AuthUser,
  AuthState,
  AuthContextValue,
  AuthProviderProps,
  PortalType,
  RegisterData,
} from "../types";
import { hasPortalAccess as hasPortalAccessUtil } from "../utils";

// ==========================================================================
// Context
// ==========================================================================

export const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined
);

// ==========================================================================
// Hook
// ==========================================================================

/**
 * Hook to access auth context
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

// ==========================================================================
// Types for Provider Configuration
// ==========================================================================

/**
 * Auth API interface that consumers must implement
 */
export interface AuthApi {
  isAuthenticated: () => boolean;
  login: (params: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }) => Promise<AuthResponse>;
  register: (data: RegisterData) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  forgotPassword: (params: { email: string }) => Promise<void>;
  resetPassword: (params: {
    token: string;
    newPassword: string;
  }) => Promise<void>;
  getProfile: () => Promise<UserProfile>;

  // ------------------------------------------------------------------------
  // REQ-BVDR-AUTH-BOOTSTRAP-401-LOOP-FIX (PC-011): NARROW bootstrap hooks
  // ------------------------------------------------------------------------
  // These optional methods drive the non-navigating bootstrap state machine.
  // When BOTH are provided, `AuthProvider.initialize` uses the refresh-then-
  // retry flow that handles a bootstrap `/auth/me` 401 locally (no reload).
  // When ABSENT, the provider falls back to the legacy single-`getProfile`
  // flow (back-compat — preserves existing behavior and existing tests).

  /**
   * Bootstrap-only `/auth/me` call that MUST route through the non-navigating
   * fetch path (`createPortalFetch(..., { navigateOn401: false })`). A 401 from
   * this call is returned to the state machine instead of hard-navigating.
   * Should throw a `BootstrapUnauthorizedError` (or any error whose
   * `status === 401` / `isUnauthorized === true`) on a 401 so the state machine
   * can distinguish "needs refresh" from a transport error.
   */
  getProfileNoRedirect?: () => Promise<UserProfile>;

  /**
   * Synchronous pre-check for refresh-token presence. Returns `true` if a
   * non-empty `refresh_token` is stored. Used to skip a doomed `/auth/refresh`
   * round-trip when no refresh token exists (AC-2 → zero refresh calls).
   */
  hasRefreshToken?: () => boolean;

  /**
   * Clear stored tokens. Invoked by the bootstrap state machine on a terminal
   * 401 (missing/rejected refresh) so the session is cleared before the login
   * form renders. Optional for back-compat; `refreshToken()` already clears on
   * its own failure, this is a defensive belt-and-suspenders clear.
   */
  clearTokens?: () => void | Promise<void>;
}

/**
 * Error thrown by `getProfileNoRedirect` (and recognized by the bootstrap
 * state machine) to mark a 401 that should trigger refresh-then-retry.
 * Consumers MAY throw this, or any error carrying `status === 401` /
 * `statusCode === 401` / `isUnauthorized === true`.
 */
export class BootstrapUnauthorizedError extends Error {
  public readonly status = 401;
  public readonly isUnauthorized = true;

  constructor(message = "Unauthorized") {
    super(message);
    this.name = "BootstrapUnauthorizedError";
  }
}

/**
 * Detect whether a thrown error represents an HTTP 401 (the signal the
 * bootstrap state machine uses to decide "attempt refresh"). Recognizes
 * `BootstrapUnauthorizedError`, `AuthApiError`-shaped errors, and any object
 * exposing `status`/`statusCode === 401` or `isUnauthorized === true`.
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof BootstrapUnauthorizedError) {
    return true;
  }
  if (typeof error === "object" && error !== null) {
    const e = error as {
      status?: number;
      statusCode?: number;
      isUnauthorized?: boolean;
    };
    return e.status === 401 || e.statusCode === 401 || e.isUnauthorized === true;
  }
  return false;
}

/**
 * Auth response from API
 */
export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  };
  profile: {
    companyId: string | null;
    companyName: string | null;
    userType: AuthUser["userType"];
    tierLevel: number;
    canAccessApal: boolean;
    canAccessHumanda: boolean;
  };
}

/**
 * User profile from API
 */
export interface UserProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  companyId: string | null;
  companyName: string | null;
  departmentId: string | null;
  userType: AuthUser["userType"];
  tierLevel: number;
  canAccessApal: boolean;
  canAccessHumanda: boolean;
  createdAt: string;
}

/**
 * Error handler function type
 */
export type AuthErrorHandler = (error: unknown) => string;

/**
 * Extended props for AuthProvider with API injection
 */
export interface ConfigurableAuthProviderProps extends AuthProviderProps {
  /** Auth API implementation */
  authApi: AuthApi;
  /** Error handler function */
  errorHandler?: AuthErrorHandler;
  /** Logger function */
  logger?: {
    debug: (message: string, data?: Record<string, unknown>) => void;
  };
}

// ==========================================================================
// Transform Functions
// ==========================================================================

/**
 * Transform AuthResponse to AuthUser
 */
export function transformAuthResponseToUser(response: AuthResponse): AuthUser {
  return {
    id: response.user.id,
    email: response.user.email,
    firstName: response.user.firstName,
    lastName: response.user.lastName,
    displayName:
      response.user.firstName && response.user.lastName
        ? `${response.user.firstName} ${response.user.lastName}`
        : null,
    avatarUrl: null,
    companyId: response.profile.companyId,
    companyName: response.profile.companyName,
    departmentId: null,
    userType: response.profile.userType,
    tierLevel: response.profile.tierLevel,
    canAccessApal: response.profile.canAccessApal,
    canAccessHumanda: response.profile.canAccessHumanda,
    portalAccess: [
      ...(response.profile.canAccessApal ? (["APAL"] as PortalType[]) : []),
      ...(response.profile.canAccessHumanda ? (["HUMANDA"] as PortalType[]) : []),
    ],
  };
}

/**
 * Transform UserProfile to AuthUser
 */
export function transformUserProfileToUser(profile: UserProfile): AuthUser {
  return {
    id: profile.id,
    email: profile.email,
    firstName: profile.firstName,
    lastName: profile.lastName,
    displayName: profile.displayName,
    avatarUrl: profile.avatarUrl,
    companyId: profile.companyId,
    companyName: profile.companyName,
    departmentId: profile.departmentId,
    userType: profile.userType,
    tierLevel: profile.tierLevel,
    canAccessApal: profile.canAccessApal,
    canAccessHumanda: profile.canAccessHumanda,
    portalAccess: [
      ...(profile.canAccessApal ? (["APAL"] as PortalType[]) : []),
      ...(profile.canAccessHumanda ? (["HUMANDA"] as PortalType[]) : []),
    ],
  };
}

// ==========================================================================
// Default Error Handler
// ==========================================================================

const defaultErrorHandler: AuthErrorHandler = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
};

// ==========================================================================
// Default Logger
// ==========================================================================

const defaultLogger = {
  debug: (_message: string, _data?: Record<string, unknown>): void => {
    // Silent by default
  },
};

// ==========================================================================
// Single-flight bootstrap refresh (REQ-BVDR-AUTH-BOOTSTRAP-401-LOOP-FIX)
// ==========================================================================

/**
 * Module-level in-flight refresh promise. Scoped here (NOT inside the per-render
 * closure) so any concurrent/duplicate refresh trigger within one bootstrap
 * cycle — e.g. a React-StrictMode double-invoke of `initialize` — shares a
 * SINGLE `/auth/refresh` round-trip. The promise is cleared once it settles so
 * a genuinely new bootstrap cycle can refresh again.
 *
 * AC-8: exactly one `/auth/refresh` per cluster of concurrent triggers.
 */
let inFlightRefresh: Promise<void> | null = null;

/**
 * Run `refreshToken` under the single-flight guard. The first caller starts the
 * refresh; concurrent callers await the SAME promise. The guard is cleared in a
 * `finally` so the next distinct bootstrap cycle starts fresh.
 */
function singleFlightRefresh(refreshToken: () => Promise<void>): Promise<void> {
  if (inFlightRefresh === null) {
    inFlightRefresh = (async (): Promise<void> => {
      try {
        await refreshToken();
      } finally {
        inFlightRefresh = null;
      }
    })();
  }
  return inFlightRefresh;
}

// ==========================================================================
// Provider Component
// ==========================================================================

/**
 * AuthProvider with configurable API
 *
 * @example
 * ```tsx
 * import { AuthProvider } from "@apal/shared-auth";
 * import { authApi, handleAuthError } from "./lib/auth/api";
 *
 * function App() {
 *   return (
 *     <AuthProvider
 *       authApi={authApi}
 *       errorHandler={handleAuthError}
 *     >
 *       {children}
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export function AuthProvider({
  children,
  authApi,
  errorHandler = defaultErrorHandler,
  logger = defaultLogger,
}: ConfigurableAuthProviderProps): React.JSX.Element {
  const [state, setState] = React.useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  // Initialize auth state on mount
  React.useEffect(() => {
    // Render the login form as the terminal "no session" state WITHOUT any
    // hard navigation / reload. This is the bootstrap fall-through (AC-2/AC-3).
    const renderLoggedOut = (): void => {
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    };

    const renderAuthenticated = (profile: UserProfile): void => {
      const user = transformUserProfileToUser(profile);
      logger.debug("[AuthProvider] User authenticated", { userId: user.id });
      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        error: null,
      });
    };

    /**
     * NARROW-BOOTSTRAP state machine (REQ-BVDR-AUTH-BOOTSTRAP-401-LOOP-FIX).
     * Used when the adapter provides `getProfileNoRedirect` (+ `hasRefreshToken`).
     *
     * Flow on bootstrap `/auth/me`:
     *   200            → authenticated.
     *   401:
     *     no refresh   → ZERO `/auth/refresh`, clear + login (AC-2).
     *     refresh OK   → ONE `/auth/refresh`, retry `/auth/me` ONCE; on 200
     *                    authenticated, on 401 again clear + login (AC-1).
     *     refresh FAIL → ONE terminal `/auth/refresh`, clear + login (AC-3/AC-9).
     * Never hard-navigates / reloads in the bootstrap path → no loop (AC-6).
     */
    const initializeNarrowBootstrap = async (
      getProfileNoRedirect: () => Promise<UserProfile>
    ): Promise<void> => {
      // First bootstrap /auth/me attempt (non-navigating).
      let firstError: unknown;
      try {
        const profile = await getProfileNoRedirect();
        renderAuthenticated(profile);
        return;
      } catch (error) {
        firstError = error;
      }

      // Any non-401 transport error is terminal → clear + login (no loop).
      if (!isUnauthorizedError(firstError)) {
        logger.debug("[AuthProvider] Bootstrap profile fetch failed (non-401)", {
          error: firstError,
        });
        await safeClearTokens();
        renderLoggedOut();
        return;
      }

      // 401 → pre-check refresh-token presence. Absent ⇒ ZERO refresh calls.
      const refreshAvailable = authApi.hasRefreshToken
        ? authApi.hasRefreshToken()
        : false;

      if (!refreshAvailable) {
        logger.debug(
          "[AuthProvider] Bootstrap 401 with no refresh token — clearing session"
        );
        await safeClearTokens();
        renderLoggedOut();
        return;
      }

      // Present refresh token ⇒ exactly ONE refresh round-trip (single-flight).
      try {
        await singleFlightRefresh(() => authApi.refreshToken());
      } catch (refreshError) {
        // Refresh 401 is TERMINAL (endpoint exclusion — no refresh→refresh loop).
        // `refreshToken()` already clears tokens on failure; clear defensively.
        logger.debug("[AuthProvider] Bootstrap refresh failed — clearing session", {
          error: refreshError,
        });
        await safeClearTokens();
        renderLoggedOut();
        return;
      }

      // Refresh succeeded → retry `/auth/me` EXACTLY ONCE.
      try {
        const profile = await getProfileNoRedirect();
        renderAuthenticated(profile);
      } catch (retryError) {
        // A re-failed retry is terminal (clear + login), never a re-loop.
        logger.debug(
          "[AuthProvider] Bootstrap retry after refresh failed — clearing session",
          { error: retryError }
        );
        await safeClearTokens();
        renderLoggedOut();
      }
    };

    /**
     * LEGACY single-`getProfile` flow (back-compat). Used when the adapter does
     * NOT provide `getProfileNoRedirect`. Behavior is unchanged from before this
     * REQ — preserves existing consumers and existing tests.
     */
    const initializeLegacy = async (): Promise<void> => {
      try {
        const profile = await authApi.getProfile();
        renderAuthenticated(profile);
      } catch (error) {
        logger.debug("[AuthProvider] Failed to get user profile", { error });
        renderLoggedOut();
      }
    };

    const safeClearTokens = async (): Promise<void> => {
      if (!authApi.clearTokens) {
        return;
      }
      try {
        await authApi.clearTokens();
      } catch (error) {
        logger.debug("[AuthProvider] clearTokens failed during bootstrap", {
          error,
        });
      }
    };

    const initialize = async (): Promise<void> => {
      logger.debug("[AuthProvider] Initializing auth state");

      // No token present ⇒ login immediately, ZERO `/auth/me` calls (AC-4).
      if (!authApi.isAuthenticated()) {
        logger.debug(
          "[AuthProvider] No token found, user is not authenticated"
        );
        renderLoggedOut();
        return;
      }

      // Prefer the narrow non-navigating bootstrap ONLY when the adapter
      // provides BOTH hooks (per the AuthApi contract above, line 77: "When
      // BOTH are provided"). The narrow flow's 401 path depends on
      // `hasRefreshToken` to decide whether a refresh is available
      // (:418-420) — without it, `refreshAvailable` defaults to `false` and a
      // user holding a VALID refresh token would be force-logged-out. So when
      // either hook is missing, fall back to the SAFE legacy single-getProfile
      // flow (back-compat) rather than the partial narrow path.
      if (
        typeof authApi.getProfileNoRedirect === "function" &&
        typeof authApi.hasRefreshToken === "function"
      ) {
        await initializeNarrowBootstrap(authApi.getProfileNoRedirect);
      } else {
        await initializeLegacy();
      }
    };

    initialize();
  }, [authApi, logger]);

  // Login action
  const login = React.useCallback(
    async (
      email: string,
      password: string,
      rememberMe = false
    ): Promise<{ success: boolean; error?: string }> => {
      setState((prev) => ({ ...prev, error: null }));

      try {
        const response = await authApi.login({ email, password, rememberMe });
        const user = transformAuthResponseToUser(response);

        logger.debug("[AuthProvider] Login successful", { userId: user.id });

        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          error: null,
        });

        return { success: true };
      } catch (error) {
        const errorMessage = errorHandler(error);
        logger.debug("[AuthProvider] Login failed", { error: errorMessage });

        setState((prev) => ({
          ...prev,
          error: errorMessage,
        }));

        return { success: false, error: errorMessage };
      }
    },
    [authApi, errorHandler, logger]
  );

  // Register action
  const register = React.useCallback(
    async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
      setState((prev) => ({ ...prev, error: null }));

      try {
        const response = await authApi.register(data);
        const user = transformAuthResponseToUser(response);

        logger.debug("[AuthProvider] Registration successful", {
          userId: user.id,
        });

        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          error: null,
        });

        return { success: true };
      } catch (error) {
        const errorMessage = errorHandler(error);
        logger.debug("[AuthProvider] Registration failed", {
          error: errorMessage,
        });

        setState((prev) => ({
          ...prev,
          error: errorMessage,
        }));

        return { success: false, error: errorMessage };
      }
    },
    [authApi, errorHandler, logger]
  );

  // Logout action
  const logout = React.useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
      logger.debug("[AuthProvider] Logout successful");
    } catch (error) {
      // Even if logout fails on server, clear local state
      logger.debug(
        "[AuthProvider] Logout request failed, clearing local state",
        { error }
      );
    }

    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  }, [authApi, logger]);

  // Refresh token action
  const refreshToken = React.useCallback(async (): Promise<boolean> => {
    try {
      await authApi.refreshToken();
      logger.debug("[AuthProvider] Token refreshed successfully");
      return true;
    } catch (error) {
      logger.debug("[AuthProvider] Token refresh failed", { error });

      // Clear auth state on refresh failure
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });

      return false;
    }
  }, [authApi, logger]);

  // Forgot password action
  const forgotPassword = React.useCallback(
    async (email: string): Promise<{ success: boolean; error?: string }> => {
      try {
        await authApi.forgotPassword({ email });
        logger.debug("[AuthProvider] Forgot password request sent");
        return { success: true };
      } catch (error) {
        const errorMessage = errorHandler(error);
        logger.debug("[AuthProvider] Forgot password failed", {
          error: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    },
    [authApi, errorHandler, logger]
  );

  // Reset password action
  const resetPassword = React.useCallback(
    async (
      token: string,
      newPassword: string
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        await authApi.resetPassword({ token, newPassword });
        logger.debug("[AuthProvider] Password reset successful");
        return { success: true };
      } catch (error) {
        const errorMessage = errorHandler(error);
        logger.debug("[AuthProvider] Password reset failed", {
          error: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    },
    [authApi, errorHandler, logger]
  );

  // Update profile (refresh user data)
  const updateProfile = React.useCallback(async (): Promise<void> => {
    if (!state.isAuthenticated) {
      return;
    }

    try {
      const profile = await authApi.getProfile();
      const user = transformUserProfileToUser(profile);

      setState((prev) => ({
        ...prev,
        user,
      }));
    } catch (error) {
      logger.debug("[AuthProvider] Failed to update profile", { error });
    }
  }, [authApi, logger, state.isAuthenticated]);

  // Clear error
  const clearError = React.useCallback((): void => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Portal access helper using the utility function
  const hasPortalAccess = React.useCallback(
    (portal: PortalType): boolean => {
      return hasPortalAccessUtil(state.user, portal);
    },
    [state.user]
  );

  const value: AuthContextValue = {
    ...state,
    login,
    register,
    logout,
    refreshToken,
    forgotPassword,
    resetPassword,
    updateProfile,
    clearError,
    hasPortalAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
